import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import api from '../../services/api';

export default function AdminContent() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [saveToast, setSaveToast] = useState(false);

  useEffect(() => {
    api.getCmsPages()
      .then(res => setPages(res.pages || []))
      .catch(err => console.error('Error loading pages:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await api.updateCmsPage(selectedPage.id, {
        hero_headline: selectedPage.hero_headline,
        meta_description: selectedPage.meta_description
      });
      setPages(prev => prev.map(p => p.id === selectedPage.id ? selectedPage : p));
      setSelectedPage(null);
      setSaveToast(true);
      setTimeout(() => setSaveToast(false), 2500);
    } catch (err) {
      alert(err.message || 'Failed to save page.');
    }
  };

  return (
    <div className="space-y-8 font-sans">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-serif font-bold text-white">Pages & Content Management</h2>
          <p className="text-xs text-zinc-500 mt-1 font-light">Edit hero headlines and SEO meta descriptions for main site routes.</p>
        </div>
      </div>

      {saveToast && (
        <div className="p-4 bg-emerald-950/30 border border-emerald-500/20 text-emerald-400 text-xs rounded-xl flex items-center gap-2">
          <SafeIcon name="CheckCircle" className="w-4 h-4" />
          Page content updated successfully.
        </div>
      )}

      {/* Pages Table */}
      <div className="bg-zinc-950 border border-zinc-900 rounded-3xl overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-zinc-900/50 text-zinc-400 text-[10px] font-bold uppercase tracking-wider border-b border-zinc-900">
              <tr>
                <th className="p-4 pl-6">Page Name</th>
                <th className="p-4">Route Path</th>
                <th className="p-4">Hero Headline</th>
                <th className="p-4">Status</th>
                <th className="p-4">Last Updated</th>
                <th className="p-4 pr-6 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900 text-zinc-300">
              {loading ? (
                <tr><td colSpan="6" className="py-16 text-center text-zinc-600 text-xs">Loading pages...</td></tr>
              ) : pages.map(page => (
                <tr key={page.id} className="hover:bg-zinc-900/20 transition-colors">
                  <td className="p-4 pl-6 font-bold text-white">{page.name}</td>
                  <td className="p-4 font-mono text-zinc-500">{page.path}</td>
                  <td className="p-4 max-w-xs truncate text-zinc-400">{page.hero_headline}</td>
                  <td className="p-4">
                    <span className="px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[9px] font-bold uppercase tracking-widest border border-emerald-500/20">
                      {page.status}
                    </span>
                  </td>
                  <td className="p-4 text-zinc-500 text-[11px]">{new Date(page.updated_at).toLocaleDateString()}</td>
                  <td className="p-4 pr-6 text-right">
                    <button
                      onClick={() => setSelectedPage({ ...page })}
                      className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-colors"
                    >
                      Edit Copy
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Page Modal */}
      {selectedPage && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-900 rounded-3xl max-w-xl w-full p-8 space-y-6 shadow-2xl">
            <div className="flex justify-between items-center border-b border-zinc-900 pb-4">
              <h3 className="text-lg font-bold text-white">Edit {selectedPage.name} Content</h3>
              <button onClick={() => setSelectedPage(null)} className="text-zinc-500 hover:text-white">
                <SafeIcon name="X" className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Route Path</label>
                <input
                  type="text"
                  className="input-field font-mono text-zinc-500"
                  value={selectedPage.path}
                  readOnly
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Hero Headline Copy</label>
                <textarea
                  rows={2}
                  className="input-field resize-none"
                  value={selectedPage.hero_headline}
                  onChange={e => setSelectedPage({ ...selectedPage, hero_headline: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-zinc-400 uppercase">Meta Description (SEO)</label>
                <textarea
                  rows={3}
                  className="input-field resize-none"
                  value={selectedPage.meta_description}
                  onChange={e => setSelectedPage({ ...selectedPage, meta_description: e.target.value })}
                  required
                />
              </div>

              <div className="flex gap-3 pt-4 border-t border-zinc-900">
                <button
                  type="button"
                  onClick={() => setSelectedPage(null)}
                  className="w-1/2 bg-zinc-900 text-zinc-400 hover:text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs shadow-lg shadow-blue-600/20"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
